import { Circle, Group, Line, Text } from "react-konva";
import type { BoardToken } from "@/lib/canvas/board-schema";
import { pointToPx, type Dims } from "@/lib/canvas/coords";

/**
 * Player / ball token renderer for the tactical board.
 *
 * - **Player**: filled circle ~3% of the longer axis in radius +
 *   centered label text. The label is typically a jersey number ("9")
 *   or role string ("GK") from `FORMATION_PRESETS`. The fill is the
 *   token's `color` (the coach's selected swatch) when set — so two
 *   teams can be placed in different colors — else the default cyan.
 * - **Ball**: a small soccer ball — white circle with the classic
 *   black centre pentagon and seam spokes to the rim. The ball has
 *   no color override; it always reads as a ball.
 *
 * Selection state: a thin amber outline ring around the token. Same
 * convention as `ShapeRenderer`'s selection treatment.
 */

const SELECTED_COLOR = "#fbbf24";
const PLAYER_FILL = "#0ea5e9";
const PLAYER_BORDER = "rgba(15, 23, 42, 0.75)";
const PLAYER_LABEL = "#ffffff";
const BALL_FILL = "#ffffff";
const BALL_BORDER = "#0f172a";
const BALL_PATTERN = "#0f172a";

export type TokenRendererProps = {
  token: BoardToken;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (token: BoardToken) => void;
};

export function TokenRenderer(props: TokenRendererProps) {
  const { token, dims, selected, onSelect, draggable, onDragEnd } = props;
  const center = pointToPx({ x: token.x, y: token.y }, dims);
  const radius = Math.min(dims.width, dims.height) * 0.028;

  if (token.kind === "ball") {
    const r = radius * 0.78;
    // Centre black pentagon — 5 vertices, point-up.
    const pentR = r * 0.42;
    const pentagon: number[] = [];
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
      pentagon.push(Math.cos(a) * pentR, Math.sin(a) * pentR);
    }
    // Seam spokes from each pentagon vertex out toward the rim —
    // suggest the surrounding panels without drawing every hexagon.
    const seams = Array.from({ length: 5 }, (_, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
      return [
        Math.cos(a) * pentR,
        Math.sin(a) * pentR,
        Math.cos(a) * r * 0.92,
        Math.sin(a) * r * 0.92,
      ];
    });
    return (
      <Group
        id={token.id}
        x={center.x}
        y={center.y}
        onClick={onSelect}
        onTap={onSelect}
        draggable={draggable}
        onDragStart={onSelect}
        onDragEnd={(e) => {
          if (!onDragEnd) return;
          const newX = clamp01(e.target.x() / dims.width);
          const newY = clamp01(e.target.y() / dims.height);
          // Pin the node to the committed position: when a clamped
          // commit EQUALS the previous props (e.g. dragged past the
          // same edge twice), react-konva sees no prop delta and
          // would leave the node stranded where the pointer dropped.
          e.target.position({ x: newX * dims.width, y: newY * dims.height });
          onDragEnd({ ...token, x: newX, y: newY });
        }}
      >
        {/* White leather */}
        <Circle
          radius={r}
          fill={BALL_FILL}
          stroke={selected ? SELECTED_COLOR : BALL_BORDER}
          strokeWidth={selected ? 3 : 1.5}
        />
        {/* Seam spokes (under the pentagon so the pentagon caps
         * them cleanly at the centre). */}
        {seams.map((pts, i) => (
          <Line
            key={`ball-seam-${i}`}
            points={pts}
            stroke={BALL_PATTERN}
            strokeWidth={Math.max(1, r * 0.13)}
            lineCap="round"
            listening={false}
          />
        ))}
        {/* Centre black pentagon */}
        <Line
          points={pentagon}
          closed
          fill={BALL_PATTERN}
          listening={false}
        />
      </Group>
    );
  }

  // Player token
  const label = token.label?.trim() ?? "";
  // Pre-measure: short labels fit in a smaller font, longer ones
  // shrink to stay inside the circle.
  const fontSize = label.length > 2 ? Math.max(11, radius * 0.8) : Math.max(13, radius * 1.1);

  return (
    <Group
      id={token.id}
      x={center.x}
      y={center.y}
      onClick={onSelect}
      onTap={onSelect}
      draggable={draggable}
      onDragStart={onSelect}
      onDragEnd={(e) => {
        if (!onDragEnd) return;
        const newX = clamp01(e.target.x() / dims.width);
        const newY = clamp01(e.target.y() / dims.height);
        onDragEnd({ ...token, x: newX, y: newY });
      }}
    >
      <Circle
        radius={radius}
        fill={token.color ?? PLAYER_FILL}
        stroke={selected ? SELECTED_COLOR : PLAYER_BORDER}
        strokeWidth={selected ? 3 : 1.5}
      />
      {label ? (
        <Text
          text={label}
          fontSize={fontSize}
          fontStyle="700"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fill={PLAYER_LABEL}
          // Centre the text: offset by half its measured width/height.
          // Konva can't compute measureText synchronously without a
          // ref, so use an approximation that's tight for 1-3 char
          // labels (the common case from FORMATION_PRESETS).
          offsetX={(label.length * fontSize * 0.32)}
          offsetY={fontSize * 0.5}
          listening={false}
        />
      ) : null}
    </Group>
  );
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
